import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Paper, Typography, Box, Tooltip, LinearProgress, Button } from '@mui/material';
import logo from '../../assets/images/Supervoima-Logo-White.svg';
import AdoptionCurveChart from './AdoptionCurveChart';
import AdoptionBreakdownSection from './AdoptionBreakdownSection';

// Progress tier configuration (shared with QuestionsSection)
const BASELINE_QUESTIONS = 39;

const getProgressTier = (percentage) => {
  if (percentage >= 100) {
    return { label: 'Comprehensive AI Profile', color: '#2E7D32' };
  }
  if (percentage > 75) {
    return { label: 'Broad AI Profile', color: '#2E7D32' };
  }
  if (percentage >= 50) {
    return { label: 'Maturing AI Profile', color: '#43A047' };
  }
  if (percentage >= 25) {
    return { label: 'Basic AI Profile', color: '#ED6C02' };
  }
  return { label: 'Incomplete AI Profile', color: '#D32F2F' };
};

export default function SummarySection({
  dimensionSummaries,
  dimensionMapping,
  summary,
  scope = 'personal',
  companyName = '',
  userName = '',
  overallAssessment = null,
  dimensionInsights = {},
  benchmarkScore = 2.5
}) {
  const navigate = useNavigate();

  // Progress tier for personal scope
  const questionCount = summary?.totalResponses || 0;
  const progressPercentage = Math.min(100, Math.round((questionCount / BASELINE_QUESTIONS) * 100));
  const progressTier = getProgressTier(progressPercentage);

  const getHeaderText = () => {
    if (scope === 'personal') {
      return 'AI Scorecard';
    }
    // For organization scope, companyName should now be the organization name, not org_id
    const name = companyName || 'Organisation';
    return `AI Scorecard - ${name}`;
  };

  // TypeScript helpers (drop-in)
  function weightedAssessedMean(dims) {
    let wSum = 0, w = 0;
    for (const d of dims) {
      if (d.axisCount > 0 && isFinite(d.avgScore)) {
        wSum += d.avgScore * d.axisCount;
        w += d.axisCount;
      }
    }
    return w > 0 ? wSum / w : 0; // 0..5
  }

  function designEffect(m = 12, rho = 0.30) {
    return 1 + (Math.max(1, m) - 1) * rho;
  }

  function effectiveN(totalAnswers, m = 12, rho = 0.30) {
    return totalAnswers / designEffect(m, rho);
  }

  function marginOfError95(n_eff, sigma = 15) {
    if (n_eff <= 0) return Infinity;
    return 1.96 * sigma / Math.sqrt(n_eff); // points on 0..100
  }

  function coveragePenalty(totalAnswers, N_full = 60) {
    const r = Math.min(Math.max(totalAnswers / N_full, 0), 1);
    return 1 - Math.pow(1 - r, 2); // quadratic fade
  }

  function confidenceLevel(totalAnswers, perDimCounts, m = 12, rho = 0.30) {
    if (totalAnswers < 10) return { level: "Insufficient", text: `Not enough data (${totalAnswers} answers)` };

    const nEff = effectiveN(totalAnswers, m, rho);
    const ME = marginOfError95(nEff, 15); // 0..100
    const minPerDim = 30;                 // gate for "Full"

    const balanced = perDimCounts.length > 0
      ? perDimCounts.every(c => c >= minPerDim)
      : false;

    if (ME <= 6)
      return { level: "High", text: `High confidence level`, answers: totalAnswers };
    if (ME <= 8)
      return { level: "Medium", text: `Medium confidence level`, answers: totalAnswers };
    if (ME <= 10)
      return { level: "Preliminary", text: `Preliminary score confidence level`, answers: totalAnswers };
    return { level: "Insufficient", text: `Score confidence level is low (too small sample)`, answers: totalAnswers };
  }

  // Coverage-adjusted scoring with saturation at 200
  function calculateCoverageAdjustedScore(dimensionSummaries, totalResponses) {
    const validDimensions = dimensionSummaries.filter(dim => dim.axisCount > 0);
    
    // Calculate assessed mean (S): weighted average of answered item scores (0–5 scale)
    const S = weightedAssessedMean(dimensionSummaries);
    
    // Coverage penalty (P): fades out as total answers accumulate; saturates at N_full = 22 (full assessment)
    const effectiveResponses = totalResponses || validDimensions.reduce((sum, d) => sum + d.axisCount, 0);
    const P = coveragePenalty(effectiveResponses, 22);
    
    // Overall Score: Overall = S × P
    const overallScore = effectiveResponses < 10 ? null : S * P;
    
    // Get margin of error based confidence level (only for org/industry reports)
    const perDimCounts = dimensionSummaries.map(d => d.axisCount);
    const confidence = scope === 'personal'
      ? { level: "Personal", text: "", answers: effectiveResponses }
      : confidenceLevel(effectiveResponses, perDimCounts);
    
    return {
      score: overallScore,
      assessedMean: S,
      confidence: confidence,
      responses: effectiveResponses,
      coverageRatio: effectiveResponses / 22
    };
  }
  
  const scoreResult = calculateCoverageAdjustedScore(dimensionSummaries, summary?.totalResponses);
  const overallPercentage = scoreResult.score !== null ? 
    Math.round((scoreResult.score / 5.0) * 100) : 
    Math.round((scoreResult.baseScore / 5.0) * 100);

  // Format date range to yyyy-mm-dd format with dash
  const formatDateRange = (dateRange) => {
    if (!dateRange) return '';
    // Handle formats like "2024-09-12T04:05:43Z to 2025-09-12T04:05:43Z"
    const dates = dateRange.split(' to ');
    if (dates.length === 2) {
      const startDate = dates[0].split('T')[0]; // Extract yyyy-mm-dd part
      const endDate = dates[1].split('T')[0];   // Extract yyyy-mm-dd part
      return `${startDate} - ${endDate}`;
    }
    return dateRange;
  };

  return (
    <Paper elevation={4} sx={{ p: 0, mb: 6, borderRadius: 3, overflow: 'hidden' }}>
      {/* Blue Header Bar with Logo and Title */}
      <Box sx={{
        backgroundColor: 'rgb(1, 28, 45)',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        px: 3
      }}>
        <img
          src={logo}
          alt="Supervoima Logo"
          style={{ height: '36px', width: 'auto', marginRight: '16px' }}
        />
        <Typography variant="h5" sx={{
          color: 'white',
          fontFamily: 'Bebas Neue',
          fontWeight: 'normal',
          textAlign: 'center',
          flexGrow: 1
        }}>
          {getHeaderText()}
        </Typography>
      </Box>

      {/* Content Area */}
      <Box sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 2, color: 'rgb(1, 28, 45)', fontFamily: 'Bebas Neue', fontWeight: 'normal', textAlign: 'center' }}>
          Overall AI Score
        </Typography>
      
      {/* Overall AI Score with Confidence */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Tooltip
          title="Overall score = weighted mean of answered items (scored 1–5) vs. maximum potential score. Overall score reaches gradually full weight at > 60 answers. Higher confidence levels correspond to lower margins of error, confidence level is based on a 95% margin of error using an effective sample size. "
          placement="top"
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '0.875rem',
                lineHeight: 1.4,
                maxWidth: '400px'
              }
            }
          }}
        >
          <Typography
            variant="h2"
            sx={{
              color: scoreResult.score === null ? '#a0aec0' : 'rgb(2,28,45)',
              opacity: scoreResult.score === null ? 0.6 : 1,
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              fontSize: '4.5rem',
              cursor: 'help'
            }}
          >
            {scoreResult.responses < 10 ? '%' : `${overallPercentage}%`}
          </Typography>
        </Tooltip>

        {/* Progress Bar for Overall Assessment */}
        {scoreResult.responses >= 10 && (
          <Box sx={{ maxWidth: 400, mx: 'auto', mt: 2, mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={overallPercentage}
              sx={{
                height: 28,
                borderRadius: 8,
                backgroundColor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: 'rgb(1, 28, 45)', // Dark blue always
                  borderRadius: 8,
                },
              }}
            />
          </Box>
        )}
        
        {/* Confidence Text Centered Below - Only for org/industry reports */}
        {scope !== 'personal' && scoreResult.responses >= 1 && scoreResult.confidence.text && (
          <>
            <Typography
              variant="body2"
              sx={{
                fontSize: '14px',
                color: '#718096',
                fontWeight: (scoreResult.confidence.level === 'Preliminary' || scoreResult.confidence.level === 'Low') ? 'bold' : 'normal',
                fontStyle: 'italic',
                mt: 1
              }}
            >
              {scoreResult.confidence.text}
            </Typography>
          </>
        )}

        {/* Overall Assessment Text from AI Evaluation */}
        {overallAssessment && (
          <Box sx={{ mt: 3, px: 4, textAlign: 'left' }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.95rem',
                lineHeight: 1.7,
                color: 'text.primary',
                borderLeft: '4px solid rgb(1, 28, 45)',
                pl: 2,
                py: 1
              }}
            >
              {overallAssessment}
            </Typography>
          </Box>
        )}
      </Box>

      {/* AI Adoption Status - Bell Curve Visualization */}
      {scoreResult.score !== null && scoreResult.responses >= 10 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: 'Bebas Neue',
              fontWeight: 'normal',
              color: 'rgb(1, 28, 45)',
              textAlign: 'center',
              mb: 2,
              letterSpacing: 1
            }}
          >
            AI ADOPTION STATUS
          </Typography>
          <AdoptionCurveChart
            score={scoreResult.score}
            benchmarkScore={benchmarkScore}
            height={250}
          />
        </Box>
      )}
      
      {/* AI Adoption Status Breakdown - Per-dimension cards with insights (org/industry only) */}
      {scope !== 'personal' && (
        <AdoptionBreakdownSection
          dimensionSummaries={dimensionSummaries}
          dimensionInsights={dimensionInsights}
          dimensionMapping={dimensionMapping}
        />
      )}
      
      {/* Summary Info Section */}
      {summary && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          mt: 3,
          pt: 2,
          borderTop: '1px solid #e0e0e0',
          flexWrap: 'wrap'
        }}>
          {/* Personal scope: Compact progress indicator */}
          {scope === 'personal' ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              position: 'relative'
            }}>
              {/* Center: Text + Progress bar */}
              <Box sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                maxWidth: 400
              }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium', textAlign: 'center' }}>
                  {progressTier.label} • {questionCount} responses
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    width: '100%',
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: '#c0c0c0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: progressTier.color,
                      borderRadius: 4
                    }
                  }}
                />
              </Box>

              {/* Right: Button */}
              <Button
                size="small"
                variant="outlined"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate('/interview');
                }}
                sx={{
                  marginLeft: 'auto',
                  borderColor: 'rgb(1, 28, 45)',
                  color: 'rgb(1, 28, 45)',
                  backgroundColor: 'white',
                  borderRadius: '16px',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  px: 2,
                  py: 0.5,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    backgroundColor: 'rgb(1, 28, 45)',
                    color: 'white'
                  }
                }}
              >
                Continue Assessment
              </Button>
            </Box>
          ) : (
            /* Org scope: Keep original Unique Responses */
            <Typography variant="body2" color="text.secondary">
              Unique Responses: <span style={{ fontWeight: 'medium' }}>{summary.totalResponses}</span>
            </Typography>
          )}

          {summary.uniqueUsers > 1 && (
            <Typography variant="body2" color="text.secondary">
              Unique Users: <span style={{ fontWeight: 'medium' }}>{summary.uniqueUsers}</span>
            </Typography>
          )}
          {summary.dateRange && (
            <Typography variant="body2" color="text.secondary">
              Period: <span style={{ fontWeight: 'medium' }}>{formatDateRange(summary.dateRange)}</span>
            </Typography>
          )}
        </Box>
      )}
      </Box>
    </Paper>
  );
}
